// Copyright ©️ 2026 eVolve MEP, LLC

import { formatDate, type ShipmentPaginated } from '@evolvemep/api-ts';
import type { ColDef, ValueGetterParams } from 'ag-grid-enterprise';

import { CellRenderer } from 'components/Mantine/CellRenderer';
import { EvolveLink } from 'components/Mantine/Navigation/EvolveLink';
import { EntitySetFilterComponent } from 'helpers/ag-grid/EntitySetFilterComponent';
import { EditableShipmentStatusBadge } from 'modules/Shop/Shipments/ShipmentDetail/EditableShipmentStatusBadge';

export const getShipmentColDef = (): ColDef<ShipmentPaginated>[] => [
  {
    field: 'identifier',
    headerName: 'Shipping Order Id',
    tooltipField: 'identifier',
    width: 180,
    lockVisible: true,
    pinned: true,
    cellRenderer: CellRenderer<ShipmentPaginated>(({ value, data }) => (
      <EvolveLink to={`/shipping-orders/${data.shipmentId}`} from="Shipping Orders">
        {value}
      </EvolveLink>
    )),
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    field: 'name',
    headerName: 'Shipping Order Name',
    tooltipField: 'name',
    flex: 1,
    minWidth: 180,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    field: 'facilityName',
    headerName: 'Facility',
    tooltipField: 'facilityName',
    width: 120,
  },
  {
    field: 'projectName',
    headerName: 'Project',
    tooltipField: 'projectName',
    width: 120,
  },
  {
    field: 'shipmentStateName',
    headerName: 'Status',
    minWidth: 140,
    width: 200,
    valueGetter: ({ data }: ValueGetterParams<ShipmentPaginated>) =>
      data?.shipmentStatusName ?? data?.shipmentStateName,
    cellRenderer: CellRenderer<ShipmentPaginated>(({ data, node }) => (
      <EditableShipmentStatusBadge
        shipment={data}
        onShipmentUpdated={(updatedShipment) => node.updateData(updatedShipment)}
        editable
      />
    )),
    sortable: false,
    filter: EntitySetFilterComponent<ShipmentPaginated, 'Shipment'>('shipmentStateIds'),
  },
  {
    field: 'shipByDate',
    headerName: 'Ship By',
    width: 150,
    valueFormatter: ({ value }) => formatDate(value, 'MM/dd/yyyy', '--'),
    filter: 'agDateColumnFilter',
    filterParams: {
      filterOptions: ['inRange'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    field: 'daysRemaining',
    headerName: 'Days Remaining',
    width: 160,
    filter: 'agNumberColumnFilter',
    filterParams: {
      filterOptions: ['equals'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
];
